import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { TrendingUp, Search, CheckCircle2, XCircle, Clock, Link2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface SEOStats {
  total_keywords: number;
  active_keywords: number;
  indexed_pages: number;
  pending_indexing: number;
  failed_urls: number;
  avg_ranking: number;
  top_10_keywords: number;
}

interface SEOAutomationDashboardProps {
  userId: string;
}

export const SEOAutomationDashboard = ({ userId }: SEOAutomationDashboardProps) => {
  const [stats, setStats] = useState<SEOStats>({
    total_keywords: 0,
    active_keywords: 0,
    indexed_pages: 0,
    pending_indexing: 0,
    failed_urls: 0,
    avg_ranking: 0,
    top_10_keywords: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    
    const subscription = supabase
      .channel('seo_keywords_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'seo_keywords', filter: `agent_id=eq.${userId}` },
        () => fetchStats()
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [userId]);

  const fetchStats = async () => {
    try {
      const { data: keywords, error } = await supabase
        .from('seo_keywords')
        .select('*')
        .eq('agent_id', userId);

      if (error) throw error;

      const active = keywords?.filter(k => k.status === 'active').length || 0;
      const indexed = keywords?.filter(k => k.current_ranking !== null).length || 0;
      const pending = keywords?.filter(k => k.status === 'pending').length || 0;
      const failed = keywords?.filter(k => k.status === 'failed').length || 0;
      const top10 = keywords?.filter(k => k.current_ranking && k.current_ranking <= 10).length || 0;
      
      const rankings = keywords?.filter(k => k.current_ranking !== null).map(k => k.current_ranking!) || [];
      const avgRanking = rankings.length > 0 
        ? Math.round(rankings.reduce((a, b) => a + b, 0) / rankings.length)
        : 0;

      setStats({
        total_keywords: keywords?.length || 0,
        active_keywords: active,
        indexed_pages: indexed,
        pending_indexing: pending,
        failed_urls: failed,
        avg_ranking: avgRanking,
        top_10_keywords: top10,
      });
    } catch (error) {
      console.error('Error fetching SEO stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, description, trend }: any) => (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
          <Icon className="w-4 h-4 text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
        {trend && (
          <Badge variant="outline" className="mt-2">
            <TrendingUp className="w-3 h-3 mr-1" />
            {trend}
          </Badge>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">Loading SEO statistics...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">SEO Automation Dashboard</h2>
        <p className="text-muted-foreground">
          Real-time monitoring of your automated SEO operations
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Keywords"
          value={stats.total_keywords}
          icon={Search}
          description="Tracked across all pages"
        />
        
        <StatCard
          title="Active Keywords"
          value={stats.active_keywords}
          icon={CheckCircle2}
          description="Currently optimized"
        />
        
        <StatCard
          title="Indexed Pages"
          value={stats.indexed_pages}
          icon={Link2}
          description="Successfully indexed"
        />
        
        <StatCard
          title="Pending Indexing"
          value={stats.pending_indexing}
          icon={Clock}
          description="Awaiting processing"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          title="Average Ranking"
          value={stats.avg_ranking > 0 ? `#${stats.avg_ranking}` : '-'}
          icon={TrendingUp}
          description="Across all tracked keywords"
        />
        
        <StatCard
          title="Top 10 Rankings"
          value={stats.top_10_keywords}
          icon={CheckCircle2}
          description="Keywords in top 10"
          trend={stats.top_10_keywords > 0 ? '+' + stats.top_10_keywords : undefined}
        />
        
        <StatCard
          title="Failed URLs"
          value={stats.failed_urls}
          icon={XCircle}
          description="Requires attention"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Status Legend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2">
              <Badge className="bg-green-500">Active</Badge>
              <span className="text-sm text-muted-foreground">Optimized & indexed</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-blue-500">Pending</Badge>
              <span className="text-sm text-muted-foreground">Awaiting indexing</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-yellow-500">Processing</Badge>
              <span className="text-sm text-muted-foreground">SEO generation</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-red-500">Failed</Badge>
              <span className="text-sm text-muted-foreground">Error/unreachable</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};